import { Component } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { ContentTooltip } from 'src/app/models/content-tooltip';
import { environment } from '../../../environments/environment';
import { OwnerInfo } from '../../models/owner-info';
import { ServiceHistory } from '../../models/service-history';
import { VehicleDetail } from '../../models/vehicle-detail';
import { SAPDataService } from '../../services/sap/sap-data.service';
import { SharedStateService } from '../../services/shared-state/shared-state.service';

@Component({
    selector: 'app-customer-details',
    templateUrl: './customer-details.component.' + environment.brand + '.html',
    styleUrls: ['./customer-details.component.' + environment.brand + '.css'],
    providers: [SAPDataService]
})
export class CustomerDetailsComponent {
    ownerInfo: OwnerInfo;
    currentOdometer: number;
    odometerValidationMessage = '';
    get odometerInvalid(): boolean { return this.odometerValidationMessage != '' }
    selectedVehicleDetail: VehicleDetail;
    serviceHistories: ServiceHistory[];
    serviceHistoryLoaded = false;
    canContinueToPlan = false;
    contentTooltips: ContentTooltip[] = [];

    constructor(private sharedStateService: SharedStateService, private sapDataService: SAPDataService) {
        //console.log('CustomerDetailsComponent.constructor');

        if (!this.sharedStateService.ownerInfo) {
            this.sharedStateService.showStart();
            return;
        }

        if (this.sharedStateService.selectedVehicleDetail) {
            this.currentOdometer = this.sharedStateService.selectedVehicleDetail.currentOdometer;
        }

        this.serviceHistories = [];
        this.ownerInfo = this.sharedStateService.ownerInfo;

        // set the tooltips
        const pageName = 'Customer Details';
        this.contentTooltips = this.sharedStateService.contentTooltips.filter(x => x.pageName == pageName);
        if (this.contentTooltips.length == 0) {
            this.sharedStateService.onContentTooltipLoaded.subscribe(() => {
                this.contentTooltips = this.sharedStateService.contentTooltips.filter(x => x.pageName == pageName);
            });
        }
    }

    // table data loaded event
    onLazyLoad(): void {
        let vin = '';
        if (this.sharedStateService.selectedVehicleDetail) {
            //console.log('selecting vehicle based on selection');
            vin = this.sharedStateService.selectedVehicleDetail.vin;
        } else if (this.sharedStateService.ownerInfo) {
            //console.log('selecting vehicle based on authentication');
            vin = this.sharedStateService.ownerInfo.authVin;
        }

        this.selectedVehicleDetail = this.sharedStateService.ownerInfo.vehicleDetails.find(x => x.vin == vin);
        if (this.selectedVehicleDetail) {
            this.onSelectVehicle();
        }
    }

    // user selected a vehicle
    onSelectVehicle(): void {
        if (this.selectedVehicleDetail?.vin) {
            this.loadServiceHistory(this.selectedVehicleDetail.vin);
        }
    }

    loadServiceHistory(vin: string): void {
        this.serviceHistories = [];
        this.sharedStateService.toggleLoader(true);
        this.sapDataService.getServiceHistory(vin)
            .pipe(finalize(() => {
                this.sharedStateService.toggleLoader(false);
                if (this.currentOdometer) {
                    this.onCurrentOdometerChanged();
                }
            }))
            .subscribe(serviceHistories => {
                this.serviceHistories = serviceHistories;
                this.serviceHistoryLoaded = true;
            }, error => {
                //this.utils.log('loadServiceHistory', error);
                this.sharedStateService.showDialog({
                    isVisible: true,
                    header: 'Error getting service history',
                    message: 'Please check your details or contact us for assistance.'
                });
            });
    }

    // validation
    onCurrentOdometerChanged(): void {
        let lastOdometer = 0;
        if (this.serviceHistories?.length > 0) {
            lastOdometer = Math.max(...this.serviceHistories.map(x => +x.odometer), 0);
        }

        if (this.currentOdometer >= lastOdometer) {
            // only allow integers
            if (this.currentOdometer.toString().includes('.') || this.currentOdometer.toString().includes(',')) {
                this.odometerValidationMessage = 'Please enter a non-decimal value';
                this.canContinueToPlan = false;
                return;
            }
            // the value is valid
            this.odometerValidationMessage = '';
            this.canContinueToPlan = this.selectedVehicleDetail != undefined;
            return;
        } else {
            this.odometerValidationMessage = !this.currentOdometer ?
                'Please enter your current odometer reading' :
                'Please enter a value equal to or more than the last service KM';
            this.canContinueToPlan = false;
        }
    }

    onContinueToPlan(): void {
        if (!this.canContinueToPlan) {
            return;
        }

        this.sharedStateService.selectedVehicleDetail = this.selectedVehicleDetail;
        this.sharedStateService.selectedVehicleDetail.currentOdometer = this.currentOdometer;
        this.sharedStateService.showSelectPlan();
    }

    onBackToEnterCustomerDetails(): void {
        this.sharedStateService.showEnterCustomerDetails(true);
    }
}
